import React, { useState } from 'react';
import { Mail, Phone, Award, Book, Hospital, FileText, Microscope, Briefcase, ChevronDown, ChevronUp } from 'lucide-react';
import { AboutPageSEO } from '../../SeoTags';

const About: React.FC = () => {

  const [isEducationExpanded, setIsEducationExpanded] = useState(false);
  const [isExperienceExpanded, setIsExperienceExpanded] = useState(false);
  const [isMembershipsExpanded, setIsMembershipsExpanded] = useState(false);
  const [isAchievementsExpanded, setIsAchievementsExpanded] = useState(false);


  const services = [
    "Cirrhosis Management",
    "Hepatitis B and C Treatment",
    "Chronic Hepatitis Care",
    "Liver Cirrhosis Treatment",
    "Acute Liver Failure Management",
    "Hepatocellular Carcinoma Treatment",
    "Liver Cysts Treatment",
    "Liver Transplantation",
    "HPB Surgery",
    "GI Oncology"
  ];

  const memberships = [
    {
      org: "American Society of Transplant Surgeons (ASTS)"
    },
    {
      org: "American Society of Transplantation (AST)"
    },
    {
      org: "International Liver Transplant Society (ILTS)"
    },
    {
      org: "Intestinal Transplant and Rehabilitation Society (IRTA)"
    },
    {
      org: "The Transplantation Society (TTS)"
    },
    {
      org: "Academy of Medicine of Cleveland & Northern Ohio (AMCNO)"
    },
    {
      org: "Life member - Liver Transplantation Society of India (LTSI) No. 303"
    },
    {
      org: "Life member - Indian Society of Organ Transplantation (ISOT) No. LM 1427"
    },
    {
      org: "Life member - World Association of Laparoscopic Surgeons (WALS)"
    },
    {
      org: "Life member - Indian Chapter of the IHPBA (IC-IHPBA) No. 607"
    },
    {
      org: "Life member - Association of Surgeons of India (ASI) No. FL19677"
    },
    {
      org: "Life member - Indian Medical Association (IMA), West Bengal Chapter"
    },
    {
      org: "Diploma - National Academy of Medical Sciences, New Delhi (MNAMS)"
    },
    {
      org: "Life member - Indian Association of Gastrointestinal Endoscopic Surgeons (IAGES) No. 4987"
    },
    {
      org: "Life member - Association of Minimal Access Surgeons of India (AMASI) No. 6441"
    },
    {
      org: "Association of Robotic and Innovative Surgeons (ARIS) No. A-134"
    }
  ];

  const achievements = [
    {
      title: "Development of Pancreas/Intestinal Transplant program",
      location: "at Navi Mumbai"
    },
    {
      title: "Development of Liver Transplant program",
      location: "at MSHK, Kolkata"
    },
    {
      title: "Performed Kolkata's 1st In-House Successful Deceased Donor Liver Transplant"
    },
    {
      title: "Performed Medica's 1st In-House Successful Living Donor Liver Transplant"
    },
    {
      title: "Formulation of Donor Surgical Bundle",
      location: "at Henry Ford Hospital"
    },
    {
      title: "Experience with Transmedics Liver OCS (Liver Perfusion)",
      location: "at Henry Ford Hospital"
    },
    {
      title: "Member of team designing the Liverpedia app"
    }
  ];

  const education = [
    {
      category: "Fellowships & Advanced Training",
      items: [
        {
          degree: "Fellowship in Advanced Robotic and Innovative Surgeons (FARIS)",
          institution: "Association of Robotic and Innovative Surgeons (ARIS)",
          year: "Oct 2022 - Mar 2024"
        },
        {
          degree: "Clinical Kidney, Pancreas and Islet Transplantation (Clinical Course)",
          institution: "Leiden University Medical Center, Universiteit Leiden, the Netherlands",
          year: "September - October, 2021"
        },
        {
          degree: "Fellowship in Advanced Laparoscopic Surgery - Oncology (FALS - Oncology)",
          institution: "Indian Association of Gastrointestinal Endoscopic Surgeons (IAGES)",
          year: "May 2021"
        },
        {
          degree: "Intestinal and Multivisceral Transplant Fellowship",
          institution: "Cleveland Clinic, Cleveland, OH",
          year: "2018 - 2019"
        },
        {
          degree: "ASTS Transplant Surgery Fellowship - Liver/Kidney/HB",
          institution: "Henry Ford Hospital, Detroit, USA",
          year: "2016 - 2018"
        }
      ]
    },
    {
      category: "Certifications & Workshops",
      items: [
        {
          degree: "Gift of Life (OPO, Michigan) certified Donor surgeon",
          institution: "Liver and Kidney recovery, 2017 - present; Pancreas recovery, 2018 - present",
          year: "2017 - present"
        },
        {
          degree: "Ohio LifeBanc (OPO, Michigan) certified Donor surgeon",
          institution: "Liver and Kidney recovery",
          year: "2019 - present"
        },
        {
          degree: "Laparoscopic Donor Nephrectomy Workshop",
          institution: "ASTS",
          year: "Sept 2017 & March 2018, Oct 22, 2021"
        },
        {
          degree: "Standard ECFMG certificate & Step 3",
          institution: "United States Medical Licensing Examination (USMLE)",
          year: "May 2014; Sept 2014"
        },
        {
          degree: "BLS/ACLS",
          institution: "AHA, Henry Ford Hospital, Detroit & RTIICS, Kolkata",
          year: "2016, 2008"
        }
      ]
    },
    {
      category: "Medical Education",
      items: [
        {
          degree: "Medical Degree (MBBS)",
          institution: "Kasturba Medical College, Manipal, India",
          year: "2002 - 2007"
        },
        {
          degree: "Diplomate of National Board (D.N.B.) Residency",
          institution: "Vivekananda Institute of Medical Sciences, Kolkata, India",
          year: "2009 - 2012"
        },
        {
          degree: "Rotatory Internship",
          institution: "Medical College, Kolkata, India",
          year: "2007-08"
        },
        {
          degree: "Exchange clerkship - Thoracic Surgery",
          institution: "Massachusetts General Hospital, Boston",
          year: "2006"
        }
      ]
    }
  ];

  const experience = [
    {
      title: "Senior Consultant – Liver Transplant & HPB Surgery",
      period: "2025 - Present",
      institution: "Max Super Speciality Hospital (Shalimar Bagh & Patparganj)",
      location: "New Delhi, India",
      role: "Senior Consultant Surgeon"
    },
    {
      title: "Consultant - Multi-Organ Transplant and Hepatobiliary Surgery",
      period: "2024 Jan - 2025 Jul",
      institution: "Sir Ganga Ram Hospital",
      location: "New Delhi, India",
      role: "Consultant Surgeon"
    },
    {
      title: "Consultant - Multi-Organ Transplant, HPB and GI Surgery",
      period: "2022 - 2024",
      institution: "Medicover Hospital",
      location: "Navi Mumbai, Maharashtra, India",
      role: "Consultant Surgeon"
    },
    {
      title: "Consultant - Liver Transplant, HPB and GI Surgery",
      period: "2019 - 2022",
      institution: "Medica Superspeciality Hospital",
      location: "Kolkata, WB, India",
      role: "Chief Transplant Surgeon"
    },
    {
      title: "Intestinal and Multivisceral Transplant",
      period: "2018 - 2019",
      institution: "Cleveland Clinic",
      location: "Cleveland, OH, USA",
      role: "Fellowship (1 year)"
    },
    {
      title: "ASTS Fellowship",
      period: "July 2016 - June 2018",
      institution: "Henry Ford Hospital",
      location: "Detroit, MI, USA",
      role: "Liver/Kidney Transplant and Hepatobiliary Surgery (2 years)"
    },
    {
      title: "Fellowship",
      period: "2015 - 2016",
      institution: "Medanta - The Medicity Hospital",
      location: "Gurgaon, Haryana, India",
      role: "HB Surgery and Liver Transplantation (HBLT) (1 year)"
    },
    {
      title: "Fellowship",
      period: "July 2014 - June 2015",
      institution: "Kaohsiung Chang Gung Memorial Hospital",
      location: "Kaohsiung, Taiwan",
      role: "Liver Surgery and Liver Transplantation (1 year)"
    },
    {
      title: "Observership in Transplant Surgery",
      period: "2014",
      institution: "Medstar Georgetown Transplant Center",
      location: "Georgetown University, Washington DC",
      role: "3 weeks"
    },
    {
      title: "Resident Registrar",
      period: "2013 - 2014",
      institution: "RKMSP Hospital, VIMS",
      location: "Kolkata, India",
      role: "Department of Surgery"
    },
    {
      title: "Surgical Registrar",
      period: "2013",
      institution: "All Asia Medical Institute",
      location: "Kolkata, India",
      role: "Surgery Department"
    },
    {
      title: "Senior Resident",
      period: "2012 - 2013",
      institution: "Apollo Gleneagles Hospital",
      location: "Salt Lake, Kolkata",
      role: "Cardio Thoracic and Vascular Surgery unit"
    },
    {
      title: "D.N.B. Resident",
      period: "2009 - 2012",
      institution: "Vivekananda Institute of Medical Sciences (VIMS)",
      location: "Kolkata, India",
      role: "General Surgery"
    },
    {
      title: "Medical Officer",
      period: "2008 - 2009",
      institution: "Rabindranath Tagore International Institute of Cardiac Sciences",
      location: "Kolkata, India",
      role: "Emergency Department"
    }
  ];

  const DropdownHeader = ({
    title,
    isExpanded,
    onToggle,
    icon
  }: {
    title: string;
    isExpanded: boolean;
    onToggle: () => void;
    icon: React.ReactNode;
  }) => (
    <div
      className="flex items-center justify-between cursor-pointer bg-sage/10 p-4 rounded-xl mb-4"
      onClick={onToggle}
    >
      <div className="flex items-center">
        {icon}
        <h3 className="text-2xl font-semibold text-darkbrown ml-3">{title}</h3>
      </div>
      {isExpanded ? (
        <ChevronUp className="text-sage" />
      ) : (
        <ChevronDown className="text-sage" />
      )}
    </div>
  );

  const DropdownHeader2 = ({
    title,
    isExpanded,
    onToggle,
    icon
  }: {
    title: string;
    isExpanded: boolean;
    onToggle: () => void;
    icon: React.ReactNode;
  }) => (
    <div
      className="flex items-center justify-between cursor-pointer bg-sage/10 p-4 rounded-xl mb-4"
      onClick={onToggle}
    >
      <div className="flex items-center">
        {icon}
        <h3 className="text-2xl font-semibold text-white ml-3">{title}</h3>
      </div>
      {isExpanded ? (
        <ChevronUp className="text-sage" />
      ) : (
        <ChevronDown className="text-sage" />
      )}
    </div>
  );

  return (
    <>
    <AboutPageSEO />
    <main className="min-h-screen bg-softwhite">
      {/* Hero Section */}
      <div className="relative w-full min-h-[600px] lg:min-h-[700px] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-darkbrown/95 via-brown/90 to-charcol/85"></div>

        <div className="container mx-auto px-4 h-full flex items-center relative z-10 py-16">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16 w-full">
            <div className="w-48 h-48 md:w-64 md:h-64 lg:w-80 lg:h-80 rounded-full overflow-hidden border-4 border-palegold shadow-2xl transform hover:scale-105 transition-transform duration-300">
              <img src="/images/doc.png" alt="Dr. Hirak Pahari" className="w-full h-full object-cover" />
            </div>
            <div className="text-center lg:text-left">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-softwhite mb-4">
                Dr. Hirak Pahari
              </h1>
              <p className="text-xl md:text-2xl text-palegold mb-6">
                Consultant - Surgical Gastroenterology & Liver Transplantation

                <span className="text-lg md:text-xl mt-2 inline-block">
                  MBBS, DNB, Intestinal Transplantation (CCF,USA)
                </span>
              </p>
              <div className="flex gap-4 justify-center lg:justify-start">
                <a href="#contact" className="bg-palegold/20 backdrop-blur-sm px-8 py-3 rounded-full text-softwhite hover:bg-palegold/30 transition-all border border-palegold/40">
                  Contact
                </a>
                <a href="#expertise" className="bg-sage/90 px-8 py-3 rounded-full text-darkbrown hover:bg-sage transition-all font-medium">
                  Expertise
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16 space-y-16 -mt-16">
        {/* Biography Card */}
        <div className="relative p-1 rounded-3xl bg-gradient-to-r from-darkbrown via-sage to-palegold shadow-xl">
          <div className="bg-softwhite rounded-3xl p-8 lg:p-12">
            <h2 className="text-3xl font-bold text-darkbrown mb-6 flex items-center">
              <FileText className="mr-3 text-sage" />
              Biography
            </h2>
            <p className="text-charcol leading-relaxed text-lg">
              Dr. Hirak Pahari is a distinguished Consultant at the Institute of Surgical Gastroenterology, GI and HPB Onco-Surgery and Liver Transplantation. With specialized expertise in liver and kidney transplantation, he has completed prestigious ASTS Fellowship in Liver/Kidney Transplant and Hepatobiliary Surgery, including advanced training in Intestinal Transplant procedures. His comprehensive approach to patient care combines cutting-edge surgical techniques with compassionate treatment strategies.
            </p>
          </div>
        </div>



        {/* Contact and Expertise Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Section */}
          <div id="contact" className="bg-gradient-to-br from-darkbrown to-charcol rounded-3xl p-8 lg:p-12 text-softwhite shadow-xl">
            <h3 className="text-2xl font-semibold mb-8 flex items-center">
              <Mail className="mr-3 text-palegold" />
              Contact Information
            </h3>
            <div className="space-y-6">
              <div className="flex items-start space-x-4 group">
                <Hospital className="text-palegold group-hover:scale-110 transition-transform mt-1 flex-shrink-0" />
                <div className="text-sm space-y-1">
                  <p className="font-semibold text-base text-softwhite">Max Super Speciality Hospital</p>
                  <p className="text-softwhite/80">• Shalimar Bagh (Mon - Sat: 09:00 AM - 05:00 PM)</p>
                  <p className="text-softwhite/80">• Patparganj (Select Days: 09:00 AM - 05:00 PM)</p>
                </div>
              </div>

              <div className="flex items-center space-x-4 group">
                <Phone className="text-palegold group-hover:scale-110 transition-transform" />
                <p className="text-lg">+91 78270 39028</p>
              </div>
              <div className="flex items-center space-x-4 group">
                <Mail className="text-palegold group-hover:scale-110 transition-transform" />
                <p className="text-lg">hirak.pahari@gmail.com</p>
              </div>
            </div>
          </div>

          {/* Expertise Section */}
          <div id="expertise" className="bg-gradient-to-br from-sage/30 to-palegold/20 rounded-3xl p-8 lg:p-12 shadow-xl">
            <h3 className="text-2xl font-semibold text-darkbrown mb-8 flex items-center">
              <Hospital className="mr-3 text-sage" />
              Specializations
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-3 group"
                >
                  <div className="w-2 h-2 rounded-full bg-darkbrown group-hover:scale-150 transition-transform"></div>
                  <span className="text-charcol group-hover:text-darkbrown transition-colors">{service}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Education Section */}
        <div className="bg-gradient-to-br from-darkbrown to-charcol rounded-3xl p-8 lg:p-12 shadow-xl">
          <DropdownHeader2
            title="Education & Training"
            isExpanded={isEducationExpanded}
            onToggle={() => setIsEducationExpanded(!isEducationExpanded)}
            icon={<Book className="text-sage" />}
          />

          {isEducationExpanded && (
            <div className="space-y-8">
              {education.map((category, idx) => (
                <div key={idx} className="space-y-4">
                  <h4 className="text-xl font-semibold text-white mb-4">
                    {category.category}
                  </h4>
                  <div className="grid grid-cols-1 gap-4">
                    {category.items.map((edu, index) => (
                      <div
                        key={index}
                        className="bg-softwhite rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow"
                      >
                        <p className="text-lg font-semibold text-darkbrown">{edu.degree}</p>
                        <p className="text-charcol mt-2">{edu.institution}</p>
                        <p className="text-sage mt-1">{edu.year}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>


        <div className="bg-gradient-to-br from-sage/20 to-palegold/10 rounded-3xl p-8 lg:p-12 shadow-xl">
          <DropdownHeader
            title="Clinical Experience"
            isExpanded={isExperienceExpanded}
            onToggle={() => setIsExperienceExpanded(!isExperienceExpanded)}
            icon={<Briefcase className="text-sage" />}
          />

          {isExperienceExpanded && (
            <div className="space-y-6">
              {experience.map((exp, index) => (
                <div
                  key={index}
                  className="bg-softwhite rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow"
                >
                  <h4 className="text-lg font-semibold text-darkbrown">{exp.title}</h4>
                  <p className="text-sage mt-1">{exp.period}</p>
                  <p className="text-charcol mt-2">{exp.institution}</p>
                  <p className="text-charcol/80">{exp.location}</p>
                  <p className="text-sage/90 mt-1 font-medium">{exp.role}</p>
                </div>
              ))}
            </div>
          )}
        </div>


        {/* Certifications Section
        <div className="bg-gradient-to-br from-darkbrown to-charcol rounded-3xl p-8 lg:p-12 text-softwhite shadow-xl">
          <h3 className="text-2xl font-semibold mb-8 flex items-center">
            <Award className="mr-3 text-palegold" />
            Special Training & Certifications
          </h3>
          <div className="bg-softwhite/10 rounded-2xl p-6 backdrop-blur-sm">
            <p className="leading-relaxed text-lg">
              ASTS Fellowship - Comprehensive training in Liver/Kidney Transplant and Hepatobiliary Surgery (2023)
              with additional specialization in Intestinal Transplant procedures. This prestigious fellowship has equipped
              Dr. Pahari with advanced surgical techniques and innovative approaches to complex gastrointestinal and
              hepatobiliary conditions.
            </p>
          </div>
        </div>  */}

        <div className="bg-gradient-to-br from-darkbrown to-charcol rounded-3xl p-8 lg:p-12 shadow-xl">
          <DropdownHeader2
            title="Professional Memberships"
            isExpanded={isMembershipsExpanded}
            onToggle={() => setIsMembershipsExpanded(!isMembershipsExpanded)}
            icon={<Award className="text-sage" />}
          />

          {isMembershipsExpanded && (
            <div className="grid grid-cols-1 gap-4">
              {memberships.map((membership, index) => (
                <div
                  key={index}
                  className="bg-softwhite rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow"
                >
                  <p className="text-lg text-darkbrown">{membership.org}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-gradient-to-br from-sage/20 to-palegold/10 rounded-3xl p-8 lg:p-12 shadow-xl">
          <DropdownHeader
            title="Key Achievements"
            isExpanded={isAchievementsExpanded}
            onToggle={() => setIsAchievementsExpanded(!isAchievementsExpanded)}
            icon={<Microscope className="text-sage" />}
          />

          {isAchievementsExpanded && (
            <div className="grid grid-cols-1 gap-4">
              {achievements.map((achievement, index) => (
                <div
                  key={index}
                  className="bg-softwhite rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow"
                >
                  <p className="text-lg font-semibold text-darkbrown">{achievement.title}</p>
                  {achievement.location && (
                    <p className="text-sage mt-1">{achievement.location}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
    </>
  );
};

export default About;